import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, MagnifyingGlass, Plus, PencilSimple, Trash, UserPlus,
} from "@phosphor-icons/react";

const empty = () => ({
  sr_no: "",
  customer_id: "",
  customer_name: "",
  company_name: "",
  new_customer_phone: "",
  create_customer_master: false,
});

export default function DscList() {
  const { activeCompanyId, user } = useAuth();
  const canEdit = ["admin", "manager", "regional_manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [custMode, setCustMode] = useState("pick"); // pick | create
  const [custSearch, setCustSearch] = useState("");

  const load = async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/dsc-serials"),
        api.get("/customers"),
      ]);
      setRows(r1.data || []);
      setCustomers(r2.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeCompanyId]);

  const filteredCustomers = useMemo(() => {
    const q = custSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [customers, custSearch]);

  const nextSrNo = useMemo(() => {
    const used = new Set(rows.map((r) => Number(r.sr_no)));
    let n = 1;
    while (used.has(n)) n += 1;
    return n;
  }, [rows]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...empty(), sr_no: String(nextSrNo) });
    setCustMode("pick");
    setCustSearch("");
    setDlgOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      sr_no: String(r.sr_no || ""),
      customer_id: r.customer_id || "",
      customer_name: r.customer_name || "",
      company_name: r.company_name || "",
      new_customer_phone: "",
      create_customer_master: false,
    });
    setCustMode(r.customer_id ? "pick" : "create");
    setCustSearch("");
    setDlgOpen(true);
  };

  const pickCustomer = (c) => {
    setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, create_customer_master: false }));
    setCustSearch("");
  };

  const save = async () => {
    const sr = parseInt(form.sr_no, 10);
    if (!sr || sr < 1) { toast.error("Enter a valid Sr. No"); return; }
    if (!form.customer_name.trim()) { toast.error("Pick or type a contractor name"); return; }
    try {
      if (editing) {
        await api.patch(`/dsc-serials/${editing.id}`, {
          sr_no: sr,
          customer_id: custMode === "pick" ? form.customer_id : "",
          customer_name: form.customer_name.trim(),
          company_name: form.company_name.trim(),
        });
        toast.success("Entry updated");
      } else {
        await api.post("/dsc-serials", {
          sr_no: sr,
          customer_id: custMode === "pick" ? form.customer_id : "",
          customer_name: form.customer_name.trim(),
          company_name: form.company_name.trim(),
          new_customer_phone: form.new_customer_phone.trim(),
          create_customer_master: custMode === "create" && !!form.create_customer_master,
        });
        toast.success(
          custMode === "create" && form.create_customer_master
            ? "Entry added · customer also saved to master"
            : "Entry added"
        );
      }
      setDlgOpen(false);
      setEditing(null);
      setForm(empty());
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete Sr. No ${r.sr_no} — ${r.customer_name}?`)) return;
    try {
      await api.delete(`/dsc-serials/${r.id}`);
      toast.success("Entry deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(q) ||
      (r.company_name || "").toLowerCase().includes(q) ||
      String(r.sr_no || "").includes(q)
    );
  }, [rows, search]);

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><ShieldCheck size={11} /> DSC Directory</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">DSC List Serial Number</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manually numbered serial-book of contractors + issuing companies. Link to existing customers or create a fresh customer in one tap.
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="relative w-64 max-w-full">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Sr.No / contractor / company…"
              className="pl-8 h-9"
              data-testid="dsc-list-search"
            />
          </div>
          {canEdit && (
            <Button onClick={openAdd} className="h-9" data-testid="dsc-list-add">
              <Plus size={13} className="mr-1.5" /> Add Entry
            </Button>
          )}
          <PageExportBar
            title="DSC List Serial Number"
            filename={`dsc-list-serial-${new Date().toISOString().slice(0, 10)}`}
            columns={[
              { key: "sr_no", label: "Sr. No" },
              { key: "customer_name", label: "Name of Contractor" },
              { key: "company_name", label: "Name of Company" },
            ]}
            rows={filtered}
            testidPrefix="dsc-list"
          />
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table" data-testid="dsc-list-table">
          <thead>
            <tr>
              <th className="num" style={{ width: 72 }}>Sr. No</th>
              <th>Name of Contractor</th>
              <th>Name of Company</th>
              {canEdit && <th className="num" style={{ width: 92 }}></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canEdit ? 4 : 3} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={canEdit ? 4 : 3} className="text-center text-muted-foreground py-6">
                {search ? "No entries match that search." : "No entries yet — tap “Add Entry” to start the serial book."}
              </td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`dsc-list-row-${r.id}`}>
                <td className="num font-mono text-muted-foreground">{r.sr_no}</td>
                <td className="font-medium">
                  {r.customer_name}
                  {r.customer_id ? <span className="ml-2 text-[10.5px] font-mono uppercase tracking-wider text-primary">· master</span> : null}
                </td>
                <td>{r.company_name || "—"}</td>
                {canEdit && (
                  <td className="num">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} title="Edit" data-testid={`dsc-list-edit-${r.id}`}>
                        <PencilSimple size={13} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(r)} title="Delete" data-testid={`dsc-list-del-${r.id}`}>
                        <Trash size={13} />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={canEdit ? 4 : 3} className="text-[11.5px] font-mono text-muted-foreground py-2 px-3">
                  {filtered.length} entr{filtered.length === 1 ? "y" : "ies"}{search ? ` matching "${search}"` : ""}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <Dialog open={dlgOpen} onOpenChange={(o) => { setDlgOpen(o); if (!o) { setEditing(null); setForm(empty()); } }}>
        <DialogContent data-testid="dsc-list-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit DSC Serial Entry" : "Add DSC Serial Entry"}</DialogTitle>
            <DialogDescription>
              Enter the serial number manually. Pick a contractor from the customer master, or create a brand-new one on the fly.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-2">
            <div className="space-y-1.5">
              <Label className="eyebrow">Sr. No *</Label>
              <Input
                type="number" min="1" step="1"
                value={form.sr_no}
                onChange={(e) => setForm({ ...form, sr_no: e.target.value })}
                className="font-mono"
                data-testid="dsc-form-sr-no"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Name of Company (Issuer)</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                placeholder="e.g. eMudhra, Capricorn, IDsign…"
                data-testid="dsc-form-company"
              />
            </div>
          </div>

          <div className="space-y-2 pt-1 pb-2">
            <Label className="eyebrow">Contractor</Label>
            <div className="inline-flex rounded-md border border-border overflow-hidden text-[12.5px]">
              <button
                type="button"
                onClick={() => setCustMode("pick")}
                className={`px-3 py-1.5 ${custMode === "pick" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                data-testid="dsc-form-mode-pick"
              >
                <MagnifyingGlass size={12} className="inline mr-1" /> From master
              </button>
              <button
                type="button"
                onClick={() => setCustMode("create")}
                className={`px-3 py-1.5 ${custMode === "create" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                data-testid="dsc-form-mode-create"
              >
                <UserPlus size={12} className="inline mr-1" /> New contractor
              </button>
            </div>

            {custMode === "pick" ? (
              <div className="space-y-2">
                <Input
                  value={custSearch}
                  onChange={(e) => setCustSearch(e.target.value)}
                  placeholder="Search customer master by name or phone…"
                  className="h-9"
                  data-testid="dsc-form-cust-search"
                />
                {form.customer_name && form.customer_id ? (
                  <div className="rounded border border-primary/40 bg-primary/5 px-3 py-2 text-[12.5px] flex items-center justify-between">
                    <div>
                      <div className="font-medium">{form.customer_name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">from master · id {form.customer_id.slice(0, 8)}…</div>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => setForm({ ...form, customer_id: "", customer_name: "" })} data-testid="dsc-form-cust-clear">Change</Button>
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto rounded border border-border divide-y divide-border text-[13px]">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-3 text-muted-foreground text-[12.5px]">No customers match {custSearch ? `"${custSearch}"` : "— type a name"}.</div>
                    ) : filteredCustomers.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => pickCustomer(c)}
                        className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between"
                        data-testid={`dsc-form-cust-${c.id}`}
                      >
                        <span className="font-medium">{c.name}</span>
                        <span className="text-[11px] font-mono text-muted-foreground">{c.phone || c.gstin || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="eyebrow">Contractor Name *</Label>
                  <Input
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value, customer_id: "" })}
                    placeholder="Enter contractor name"
                    data-testid="dsc-form-new-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Phone (optional)</Label>
                  <Input
                    value={form.new_customer_phone}
                    onChange={(e) => setForm({ ...form, new_customer_phone: e.target.value })}
                    placeholder="10-digit mobile"
                    data-testid="dsc-form-new-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Save to Customer Master?</Label>
                  <div className="flex items-center gap-2 h-9">
                    <input
                      type="checkbox"
                      checked={!!form.create_customer_master}
                      onChange={(e) => setForm({ ...form, create_customer_master: e.target.checked })}
                      className="h-4 w-4"
                      data-testid="dsc-form-save-master"
                    />
                    <span className="text-[12.5px] text-muted-foreground">Add to Customers so you can reuse later</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} data-testid="dsc-form-cancel">Cancel</Button>
            <Button onClick={save} data-testid="dsc-form-save">{editing ? "Save" : "Add Entry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
